import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import FilterListIcon from '@material-ui/icons/FilterList'
import { useEffect, useRef, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { ROLES_QUERY, ROLE_MANAGEMENT_SCOPE_QUERY } from '../../graphql/role'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { RoleBrief, RoleManagementScope, RolesFilters } from '../../types/role'
import { mayCreateRoles } from '../RoleManagementForm/roleScope'
import RoleFilters from './RoleFilters'
import { RolesTable } from './RoleTable'
import useStyles from './styles/index.styles'

function RoleManagementList() {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [getRolesFilters, setGetRolesFilters] = useState<RolesFilters>({})
  const isFirstRender = useRef(true)
  const { data: scopeData } = useQuery<{
    roleManagementScope: RoleManagementScope
  }>(ROLE_MANAGEMENT_SCOPE_QUERY, { fetchPolicy: 'network-only' })
  const [getRoles, { data: rolesData, loading: loadingForRoles, refetch }] =
    useLazyQuery<{
      roles: RoleBrief[]
    }>(ROLES_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    })

  useEffect(() => {
    getRoles({
      variables: {
        filters: getRolesFilters
      }
    })
  }, [getRolesFilters.isActive])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeoutId = setTimeout(() => {
      getRoles({
        variables: {
          filters: getRolesFilters
        }
      })
    }, 1500)

    return () => clearTimeout(timeoutId)
  }, [getRolesFilters.roleName])

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Roles')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Roles List')}
        </Typography>
        <div className={classes.btnContainer}>
          <Button
            size={isSmallScreen ? 'large' : 'medium'}
            startIcon={<FilterListIcon />}
            classes={{
              startIcon: isSmallScreen ? classes.startIcon : undefined
            }}
            variant="contained"
            color="primary"
            onClick={() => setFiltersOpen(!filtersOpen)}>
            <Typography variant="h4" className={classes.btnText2}>
              {isSmallScreen ? '' : getTranslate('Filter')}
            </Typography>
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            hidden={!mayCreateRoles(scopeData?.roleManagementScope)}
            component={LocalizedLink}
            to="/access-management/role-management/create">
            <Typography variant="h4" className={classes.btnText2}>
              {getTranslate(`Add ${isSmallScreen ? '' : 'New Role'}`)}
            </Typography>
          </Button>
        </div>
      </div>
      <Collapse in={filtersOpen}>
        <RoleFilters
          getRolesFilters={getRolesFilters}
          setGetRolesFilters={setGetRolesFilters}
        />
      </Collapse>
      <RolesTable
        reloadData={refetch}
        data={rolesData?.roles}
        loading={loadingForRoles}
      />
    </div>
  )
}

export default RoleManagementList
